import { baseApi as api } from 'shared/api/baseApi';
import { getCookie } from 'shared/utils';
const injectedRtkApi = api.injectEndpoints({
	endpoints: (build) => ({
		brandsList: build.query<BrandsListApiResponse, BrandsListApiArg>({
			query: (queryArg) => ({
				url: `/api/brands/`,
				params: {
					brand_uuid: queryArg.brandUuid,
					category: queryArg.category,
					country: queryArg.country,
					limit: queryArg.limit,
					offer_uuid: queryArg.offerUuid,
					offset: queryArg.offset,
					ordering: queryArg.ordering,
					search: queryArg.search,
				},
			}),
		}),
		brandsRetrieve: build.query<BrandsRetrieveApiResponse, BrandsRetrieveApiArg>({
			query: (queryArg) => ({ url: `/api/brands/${queryArg}/` }),
		}),
		metaBrandsList: build.query<MetaBrandsListApiResponse, MetaBrandsListApiArg>({
			query: (queryArg) => ({
				url: `/api/meta/brands/`,
				params: {
					limit: queryArg.limit,
					offset: queryArg.offset,
					ordering: queryArg.ordering,
					uuid: queryArg.uuid,
				},
			}),
		}),
		metaOffersList: build.query<MetaOffersListApiResponse, MetaOffersListApiArg>({
			query: (queryArg) => ({
				url: `/api/meta/offers/`,
				params: {
					limit: queryArg.limit,
					offset: queryArg.offset,
					ordering: queryArg.ordering,
					uuid: queryArg.uuid,
				},
			}),
		}),
		offersRetrieve: build.query<OffersRetrieveApiResponse, OffersRetrieveApiArg>({
			query: (queryArg) => ({ url: `/api/offers/${queryArg}/` }),
		}),
		offersPostsSendToPmCreate: build.mutation<OffersPostsSendToPmCreateApiResponse, OffersPostsSendToPmCreateApiArg>({
			query: (queryArg) => ({
				url: `/api/offers/posts/${queryArg}/send_to_pm/`,
				method: 'POST',
				headers: { 'X-Csrftoken': getCookie('csrftoken') },
			}),
		}),
	}),
	overrideExisting: false,
});
export { injectedRtkApi as enhancedApi };
export type BrandsListApiResponse = /** status 200  */ PaginatedBrandListRead;
export type BrandsListApiArg = {
	brandUuid?: string[];
	category?: string[];
	country?: string[];
	/** Number of results to return per page. */
	limit?: number;
	offerUuid?: string[];
	/** The initial index from which to return the results. */
	offset?: number;
	/** Which field to use when ordering the results. */
	ordering?: string;
	search?: string;
};
export type BrandsRetrieveApiResponse = /** status 200  */ BrandRead;
export type BrandsRetrieveApiArg = /** A UUID string identifying this brand. */ string;
export type MetaBrandsListApiResponse = /** status 200  */ PaginatedBrandMetaListRead;
export type MetaBrandsListApiArg = {
	/** Number of results to return per page. */
	limit?: number;
	/** The initial index from which to return the results. */
	offset?: number;
	/** Which field to use when ordering the results. */
	ordering?: string;
	uuid?: string[];
};
export type MetaOffersListApiResponse = /** status 200  */ PaginatedOfferMetaListRead;
export type MetaOffersListApiArg = {
	/** Number of results to return per page. */
	limit?: number;
	/** The initial index from which to return the results. */
	offset?: number;
	/** Which field to use when ordering the results. */
	ordering?: string;
	uuid?: string[];
};
export type OffersRetrieveApiResponse = /** status 200  */ OfferRead;
export type OffersRetrieveApiArg = string;
export type OffersPostsSendToPmCreateApiResponse = unknown;
export type OffersPostsSendToPmCreateApiArg = string;
export type Category = {
	slug: string;
};
export type OfferBrand = {
	name: string;
};
export type OfferBrandRead = {
	uuid: string;
	name: string;
};
export type Currency = {
	code: string;
	sign: string;
	name: string;
};
export type Country = {
	alpha_2: string;
	name: string;
};
export type RateDetail = {
	min: string;
	max: string;
	is_percentage: boolean;
};
export type TariffDetail = {
	name: string;
	rates: RateDetail;
};
export type ActionDetail = {
	name: string;
	tariffs: TariffDetail[];
};
export type AffiliateLinkChannel = {
	name: string;
};
export type AffiliateLinkChannelRead = {
	uuid: string;
	name: string;
};
export type AffiliateLink = {
	channel: AffiliateLinkChannel;
	affiliate_link: string;
};
export type AffiliateLinkRead = {
	channel: AffiliateLinkChannelRead;
	affiliate_link: string;
};
export type Image = {
	image: string;
};
export type Post = {
	uuid?: string;
	content: string;
	images: Image[];
};
export type Offer = {
	brand: OfferBrand;
	slug: string;
	image: string;
	name: string;
	/** The cr indicator is calculated as a percentage (%). */
	cr?: string | null;
	/** The eps indicator is calculated in the currency of the offer (RUB). */
	epc?: string | null;
	/** The money_transfer_time indicator is calculated in days. */
	money_transfer_time?: string | null;
	currency: Currency;
	categories: Category[];
	countries: Country[];
	description: string;
	actual_rates: ActionDetail[];
	affiliate_links: AffiliateLink[];
	posts: Post[];
};
export type OfferRead = {
	uuid: string;
	brand: OfferBrandRead;
	slug: string;
	image: string;
	name: string;
	/** The cr indicator is calculated as a percentage (%). */
	cr?: string | null;
	/** The eps indicator is calculated in the currency of the offer (RUB). */
	epc?: string | null;
	/** The money_transfer_time indicator is calculated in days. */
	money_transfer_time?: string | null;
	currency: Currency;
	categories: Category[];
	countries: Country[];
	description: string;
	grouped_rates: {
		[key: string]: string | null;
	};
	actual_rates: ActionDetail[];
	affiliate_links: AffiliateLinkRead[];
	posts: Post[];
};
export type Brand = {
	name: string;
	image: string;
	categories: Category[];
	offers: Offer[];
};
export type BrandRead = {
	uuid: string;
	name: string;
	image: string;
	cr: string | null;
	epc: string | null;
	grouped_rates: {
		[key: string]: string | null;
	};
	categories: Category[];
	offers: OfferRead[];
};
export type PaginatedBrandList = {
	count: number;
	next?: string | null;
	previous?: string | null;
	results: Brand[];
};
export type PaginatedBrandListRead = {
	count: number;
	next?: string | null;
	previous?: string | null;
	results: BrandRead[];
};
export type BrandMeta = {
	name: string;
};
export type BrandMetaRead = {
	uuid: string;
	name: string;
};
export type PaginatedBrandMetaList = {
	count: number;
	next?: string | null;
	previous?: string | null;
	results: BrandMeta[];
};
export type PaginatedBrandMetaListRead = {
	count: number;
	next?: string | null;
	previous?: string | null;
	results: BrandMetaRead[];
};
export type OfferMeta = {
	name: string;
};
export type OfferMetaRead = {
	uuid: string;
	name: string;
};
export type PaginatedOfferMetaList = {
	count: number;
	next?: string | null;
	previous?: string | null;
	results: OfferMeta[];
};
export type PaginatedOfferMetaListRead = {
	count: number;
	next?: string | null;
	previous?: string | null;
	results: OfferMetaRead[];
};
